import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, LogIn } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      setUser(data);
      navigate(from || "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center px-4 carbon-texture">
      <div className="w-full max-w-md rounded-2xl backdrop-blur-xl bg-slate-900/80 border border-white/10 shadow-2xl p-8 animate-fade-up">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-cyan-300" />
          </span>
          <h1 className="text-2xl font-bold text-white">Giriş Yap</h1>
        </div>
        <p className="mt-2 text-sm text-slate-400">Hesabınıza giriş yaparak sorgularınızı takip edin.</p>
        {from === "/teklif-al" && (
          <div data-testid="login-teklif-notice" className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
            <p className="text-xs text-emerald-300 leading-relaxed">
              İşletmelerden teklif almak için giriş yapmanız veya kayıt olmanız gerekiyor. Giriş sonrası teklif ekranına yönlendirileceksiniz.
            </p>
            <Link data-testid="login-teklif-register-button" to="/kayit" state={{ from }}
              className="inline-flex items-center justify-center w-full py-2.5 rounded-lg bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors">
              Ücretsiz Kayıt Ol
            </Link>
          </div>
        )}

        <form onSubmit={submit} className="mt-7 space-y-5">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">E-posta</label>
            <input data-testid="login-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 focus:outline-none focus:border-cyan-400/60 transition-colors"
              placeholder="ornek@eposta.com" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Şifre</label>
              <Link data-testid="forgot-password-link" to="/sifre-sifirla"
                className="text-xs text-cyan-300 hover:text-cyan-200 transition-colors">
                Parolamı Unuttum
              </Link>
            </div>
            <input data-testid="login-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 focus:outline-none focus:border-cyan-400/60 transition-colors"
              placeholder="••••••••" />
          </div>
          <label data-testid="login-remember-me" className="flex items-center gap-2.5 cursor-pointer select-none">
            <input data-testid="login-remember-me-checkbox" type="checkbox" checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-slate-950/70 accent-cyan-400" />
            <span className="text-sm text-slate-300">Beni Hatırla</span>
            <span className="text-xs text-slate-500">(30 gün oturum açık kalır)</span>
          </label>
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <button data-testid="login-form-submit-button" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] active:scale-[0.99] transition-all disabled:opacity-50">
            <LogIn className="w-4 h-4" /> {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-400 text-center">
          Hesabınız yok mu?{" "}
          <Link data-testid="go-register-link" to="/kayit" state={{ from }} className="text-cyan-300 hover:text-cyan-200 font-semibold">
            Kayıt Olun
          </Link>
        </p>
        <p className="mt-4 text-xs text-slate-500 text-center">
          <a data-testid="login-privacy-link" href="/gizlilik-politikasi" target="_blank" rel="noopener noreferrer"
            className="hover:text-cyan-300 transition-colors underline underline-offset-2">
            Otokünye Gizlilik Politikası
          </a>
        </p>
      </div>
    </div>
  );
}
